import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiPause, FiRadio, FiLoader } from 'react-icons/fi';

const PodcastPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const podcastUrl = 'http://groupevaillanceconseil.fr/images/podcast-per.mp3';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
      setCurrentTime(audio.currentTime);
    };

    const updateCurrentTime = () => {
      setCurrentTime(audio.currentTime);
    };
    
    const handleEnd = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleReady = () => {
      setIsReady(true);
    };

    audio.addEventListener('loadedmetadata', setAudioData);
    audio.addEventListener('timeupdate', updateCurrentTime);
    audio.addEventListener('ended', handleEnd);
    audio.addEventListener('canplaythrough', handleReady);

    if (audio.readyState >= 4) {
      handleReady();
    }

    return () => {
      audio.removeEventListener('loadedmetadata', setAudioData);
      audio.removeEventListener('timeupdate', updateCurrentTime);
      audio.removeEventListener('ended', handleEnd);
      audio.removeEventListener('canplaythrough', handleReady);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !isReady) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (timeInSeconds) => {
    if (!isFinite(timeInSeconds) || timeInSeconds < 0) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleProgressClick = (e) => {
    if (!duration || !isReady) return;
    const progressBar = progressBarRef.current;
    const clickPositionX = e.pageX - progressBar.getBoundingClientRect().left;
    const progressBarWidth = progressBar.offsetWidth;
    const seekTime = (clickPositionX / progressBarWidth) * duration;
    
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const barVariants = {
    playing: (i) => ({
      scaleY: [1, 1.8, 0.5, 1.4, 1],
      transition: {
        duration: 0.9,
        repeat: Infinity,
        ease: "easeInOut",
        delay: i * 0.08,
      }
    }),
    paused: {
      scaleY: 1,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div 
      className="bg-gradient-to-br from-[#00a48c] to-[#00856f] rounded-3xl shadow-2xl p-6 text-white h-full relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="absolute top-0 left-0 w-48 h-48 bg-white/5 rounded-full -translate-x-1/3 -translate-y-1/3 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-56 h-56 bg-white/5 rounded-full translate-x-1/4 translate-y-1/4 blur-3xl"></div>
      
      <div className="relative z-10 flex flex-col h-full justify-between">
        <audio ref={audioRef} src={podcastUrl} preload="auto"></audio>
        
        <div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/30">
              <FiRadio size={24} />
            </div>
            <div>
              <h4 className="text-xl font-black">Le PER expliqué</h4>
              <p className="text-white/70 text-sm font-medium">Notre podcast de 2 minutes.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <div className="flex items-end justify-center h-20 gap-1.5 w-full">
            {[...Array(25)].map((_, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={barVariants}
                animate={isPlaying ? "playing" : "paused"}
                className="w-1.5 bg-white/50 rounded-full"
                style={{ height: `${Math.sin(i/25 * Math.PI) * 80 + 20}%` }}
              />
            ))}
          </div>
        </div>

        <div className="w-full">
          <div className="flex items-center gap-4 w-full mb-4">
            <span className="text-xs font-mono text-white/70 w-12 text-left">{formatTime(currentTime)}</span>
            <div 
              ref={progressBarRef}
              className={`w-full h-1.5 bg-white/20 rounded-full group ${isReady ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={handleProgressClick}
            >
              <div className="relative h-full">
                <div 
                  className="h-full bg-white rounded-full" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md transition-transform group-hover:scale-110"
                  style={{ left: `calc(${progressPercentage}% - 7px)` }}
                ></div>
              </div>
            </div>
            <span className="text-xs font-mono text-white/70 w-12 text-right">{formatTime(duration)}</span>
          </div>
          
          <button 
            onClick={togglePlayPause} 
            disabled={!isReady}
            className="w-16 h-16 mt-4 mx-auto flex-shrink-0 bg-white text-[#00856f] rounded-full flex items-center justify-center transition-all focus:outline-none shadow-lg disabled:opacity-70 disabled:cursor-not-allowed enabled:hover:scale-105"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {!isReady ? (
              <FiLoader className="animate-spin" size={28} />
            ) : isPlaying ? (
              <FiPause size={32} />
            ) : (
              <FiPlay size={32} className="ml-1" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PodcastPlayer;